/**
 * SVG-based architecture diagrams for blog posts.
 * Each diagram is a self-contained React component.
 */

// ── Diagram 1: API → MCP → Claude flow ───────────────────────────────────────

export function ApiToMcpFlow() {
  const nodes = [
    { label: "Your API Spec", sub: "OpenAPI / Postman\n/ Swagger", color: "#3b82f6", x: 40 },
    { label: "API to MCP", sub: "Converter", color: "#7c3aed", x: 260 },
    { label: "Live MCP Server", sub: "Hosted endpoint\nSSE + HTTP", color: "#059669", x: 480 },
    { label: "Claude / Cursor", sub: "Calls tools\nin plain English", color: "#d97706", x: 700 },
  ];
  const arrows = [
    { x1: 170, y1: 60, x2: 240, y2: 60, label: "paste URL" },
    { x1: 390, y1: 60, x2: 460, y2: 60, label: "generates" },
    { x1: 610, y1: 60, x2: 680, y2: 60, label: "connects" },
  ];

  return (
    <figure className="my-8">
      <div className="overflow-x-auto rounded-2xl border border-border bg-muted/30 p-6">
        <svg viewBox="0 0 880 140" className="w-full min-w-[560px]" role="img" aria-label="API to MCP conversion flow">
          <defs>
            <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
            </marker>
          </defs>

          {/* Arrows */}
          {arrows.map((a, i) => (
            <g key={i}>
              <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
              <text x={(a.x1 + a.x2) / 2} y={a.y1 - 8} textAnchor="middle" fontSize="10" fill="#94a3b8">{a.label}</text>
            </g>
          ))}

          {/* Nodes */}
          {nodes.map((n, i) => (
            <g key={i}>
              <rect x={n.x} y={20} width={130} height={80} rx="12" fill={n.color + "18"} stroke={n.color} strokeWidth="1.5" />
              <text x={n.x + 65} y={52} textAnchor="middle" fontSize="12" fontWeight="600" fill={n.color}>{n.label}</text>
              {n.sub.split("\n").map((line, j) => (
                <text key={j} x={n.x + 65} y={70 + j * 14} textAnchor="middle" fontSize="10" fill="#64748b">{line}</text>
              ))}
            </g>
          ))}
        </svg>
      </div>
      <figcaption className="text-center text-xs text-muted-foreground mt-3">
        The full conversion pipeline — from spec to callable tools in Claude
      </figcaption>
    </figure>
  );
}

// ── Diagram 2: MCP vs Direct API calls ───────────────────────────────────────

export function McpVsDirect() {
  return (
    <figure className="my-8">
      <div className="rounded-2xl border border-border bg-muted/30 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

          {/* Without MCP */}
          <div>
            <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-4 text-center">Without MCP</p>
            <div className="space-y-2">
              {[
                { step: "1", label: "Fetch data from API", sub: "You open dashboard / write curl" },
                { step: "2", label: "Copy the response", sub: "Select, copy, switch windows" },
                { step: "3", label: "Paste into Claude", sub: "\"Here's the data, now...\"" },
                { step: "4", label: "Claude reasons over it", sub: "Works with your snapshot" },
                { step: "5", label: "Data is already stale", sub: "It changed since you copied it" },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-950 text-red-600 dark:text-red-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {s.step}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground leading-tight">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
              <p className="text-xs text-red-600 dark:text-red-400 font-medium">5 manual steps. Snapshot data. Tedious.</p>
            </div>
          </div>

          {/* With MCP */}
          <div>
            <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wider mb-4 text-center">With MCP</p>
            <div className="space-y-2">
              {[
                { step: "1", label: "Ask Claude in plain English", sub: "\"What's the status of cus_abc123?\"" },
                { step: "2", label: "Claude calls the tool", sub: "Constructs the API request itself" },
                { step: "3", label: "MCP fetches live data", sub: "Direct call to your API right now" },
                { step: "4", label: "Claude answers you", sub: "With the current, live response" },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {s.step}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground leading-tight">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900">
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">4 steps, 1 human action. Live data. Done.</p>
            </div>
          </div>

        </div>
      </div>
      <figcaption className="text-center text-xs text-muted-foreground mt-3">
        The same lookup — with and without MCP in the loop
      </figcaption>
    </figure>
  );
}

// ── Diagram 3: Tool call sequence ─────────────────────────────────────────────

export function ToolCallFlow() {
  const lanes = [
    { label: "You", color: "#3b82f6", x: 80 },
    { label: "Claude", color: "#7c3aed", x: 280 },
    { label: "MCP Server", color: "#059669", x: 480 },
    { label: "Your API", color: "#d97706", x: 680 },
  ];
  const messages = [
    { from: 0, to: 1, y: 100, label: "\"List recent failed charges\"" },
    { from: 1, to: 2, y: 150, label: "tools/call: list_charges({status:'failed'})" },
    { from: 2, to: 3, y: 200, label: "GET /charges?status=failed" },
    { from: 3, to: 2, y: 250, label: "[{id, amount, decline_code...}]", dashed: true },
    { from: 2, to: 1, y: 300, label: "tool result: 3 failed charges", dashed: true },
    { from: 1, to: 0, y: 360, label: "\"You have 3 failed charges. The most recent...\"", dashed: true },
  ];

  return (
    <figure className="my-8">
      <div className="overflow-x-auto rounded-2xl border border-border bg-muted/30 p-6">
        <svg viewBox="0 0 800 430" className="w-full min-w-[520px]" role="img" aria-label="MCP tool call sequence diagram">
          <defs>
            <marker id="arr2" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
            </marker>
            <marker id="arr2d" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#10b981" />
            </marker>
          </defs>

          {/* Lane headers */}
          {lanes.map((l) => (
            <g key={l.label}>
              <rect x={l.x - 50} y={10} width={100} height={36} rx="8" fill={l.color + "20"} stroke={l.color} strokeWidth="1.5" />
              <text x={l.x} y={33} textAnchor="middle" fontSize="12" fontWeight="600" fill={l.color}>{l.label}</text>
              <line x1={l.x} y1={46} x2={l.x} y2={415} stroke={l.color} strokeWidth="1" strokeOpacity="0.25" strokeDasharray="4 3" />
            </g>
          ))}

          {/* Messages */}
          {messages.map((m, i) => {
            const fromX = lanes[m.from].x;
            const toX = lanes[m.to].x;
            const goRight = toX > fromX;
            const arrowX = goRight ? toX - 8 : toX + 8;
            const color = m.dashed ? "#10b981" : "#94a3b8";
            const marker = m.dashed ? "url(#arr2d)" : "url(#arr2)";
            return (
              <g key={i}>
                <line
                  x1={fromX} y1={m.y}
                  x2={arrowX} y2={m.y}
                  stroke={color} strokeWidth="1.5"
                  strokeDasharray={m.dashed ? "5 3" : undefined}
                  markerEnd={marker}
                />
                <rect
                  x={Math.min(fromX, toX) + 8}
                  y={m.y - 18}
                  width={Math.abs(toX - fromX) - 16}
                  height={14}
                  rx="3"
                  fill="var(--background, white)"
                  fillOpacity="0.85"
                />
                <text
                  x={(fromX + toX) / 2}
                  y={m.y - 7}
                  textAnchor="middle"
                  fontSize="9.5"
                  fill={color}
                  fontFamily="monospace"
                >
                  {m.label.length > 42 ? m.label.slice(0, 42) + "…" : m.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <figcaption className="text-center text-xs text-muted-foreground mt-3">
        A single conversational turn that triggers one tool call — Claude handles the protocol
      </figcaption>
    </figure>
  );
}

// ── Registry ──────────────────────────────────────────────────────────────────

export const DIAGRAMS: Record<string, React.FC> = {
  "api-to-mcp-flow": ApiToMcpFlow,
  "mcp-vs-direct": McpVsDirect,
  "tool-call-flow": ToolCallFlow,
};
